import { Box, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { StyledButton } from "../components";
import WobblyTypography from "../components/WobblyTypography";
import StyledInput from "../components/StyledInput";
import useWebsocket from "../hooks/useWebsocket";
import { WebsocketContext } from "../contexts/websocketContext";

import { MultiplayerSettingsProvider } from "../contexts/multiplayerSettingsContext";

import Lobby from "./Lobby";
import BackgroundOrbs from "../features/BackgroundOrbs";
import ProgressBar from "../features/ProgressBar";
import ForTeachers from "./ForTeachers";
import HostOnboarding from "./HostOnboarding";
import MultiplayerResultPage from "./MultiplayerResultPage";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const MultiplayerWrapper: React.FC = () => {
  return (
    <MultiplayerSettingsProvider>
      <Routes>
        <Route path="info" element={<ForTeachers />} />
        <Route path="skapa" element={<HostOnboarding />} />
        <Route path="joina" element={<PlayerOnboarding />} />
        <Route path="joina/:code" element={<PlayerOnboarding />} />
        <Route path="vanterum" element={<Lobby />} />
        <Route path="skapavanterum" element={<Lobby requestCreateRoom />} />
        <Route path="resultat" element={<MultiplayerResultPage />} />
        <Route path="*" element={<Navigate to="/multiplayer/joina" />} />
      </Routes>
    </MultiplayerSettingsProvider>
  );
};

const PlayerOnboardingSlideContext = React.createContext<{
  nextSlide: () => void;
  previousSlide: () => void;
}>({
  nextSlide: () => null,
  previousSlide: () => null,
});

const PlayerOnboarding = () => {
  const slides = [<CodeEnteringQuestion />, <NameQuestion />];
  const [slide, setSlide] = useState<number>(0);
  const { myPlayer, roomCode } = useContext(WebsocketContext);
  const { wsJoinRoom } = useWebsocket();
  const [redirect, setRedirect] = useState(false);
  const { code } = useParams();

  const nextSlide = () => {
    if (slide === slides.length - 1) {
      setRedirect(true);
    } else {
      setSlide(slide + 1);
    }
  };

  const previousSlide = () => {
    if (slide !== 0) {
      setSlide(slide - 1);
    }
  };

  useEffect(() => {
    if (code !== undefined) {
      wsJoinRoom(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (slide === 1 && myPlayer.name !== "") {
      setRedirect(true);
    }
  }, [myPlayer, slide]);

  useEffect(() => {
    if (slide === 0 && roomCode !== undefined) {
      setSlide(1);
    }
  }, [roomCode, slide]);

  if (redirect) return <Navigate to="/multiplayer/vanterum" />;

  return (
    <PlayerOnboardingSlideContext.Provider value={{ nextSlide, previousSlide }}>
      <Box position="fixed" top={0} left={0} height={6} width="100%">
        <ProgressBar steps={slides.length} filledSteps={slide} animateCurrent={true} />
      </Box>
      <Box height="80vh">{slides[slide]}</Box>
      <BackgroundOrbs />
    </PlayerOnboardingSlideContext.Provider>
  );
};

const CodeEnteringQuestion = () => {
  const [inputCode, setInputCode] = useState("");
  const { wsJoinRoom } = useWebsocket();
  const translatePageText = useTranslatePageText();

  return (
    <Box height="100%" display="flex" justifyContent="center" alignItems="center">
      <Box position="relative" zIndex={1} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h6" color="white" component="span">
          {translatePageText("onboarding.join")}
        </Typography>
        <WobblyTypography variant="h3">
          {translatePageText("onboarding.classroom")}
        </WobblyTypography>
        <Box display="flex" flexDirection="column" paddingX={2} paddingTop={4} maxWidth={350}>
          <StyledInput
            id="code"
            defaultValue={inputCode}
            placeholder={translatePageText("onboarding.classroomCode")}
            style={{ margin: "8px 0" }}
            onChange={(e: any) => setInputCode(e.target.value)}
          />
          <StyledButton
            onClick={() => {
              wsJoinRoom(inputCode);
            }}
            label={translatePageText("onboarding.joinClassroomButton")}
          />
        </Box>
        <Link
          to="/multiplayer/info"
          style={{ textDecoration: "none", marginTop: 16, color: "white" }}
        >
          <Typography variant="body2" mt={2}>
            {translatePageText("onboarding.areYouTeacher")}{" "}
            <span style={{ textDecoration: "underline" }}>
              {translatePageText("onboarding.clickHere")}
            </span>
          </Typography>
        </Link>
      </Box>
    </Box>
  );
};

const NameQuestion = () => {
  const [inputName, setInputName] = useState(undefined);
  const { wsUpdateClientData } = useWebsocket();
  const { myPlayer } = useContext(WebsocketContext);
  const translatePageText = useTranslatePageText();

  return (
    <div
      style={{
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 300,
        textAlign: "center",
        padding: 8,
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <h2>{translatePageText("onboarding.yourName")}</h2>
      <StyledInput
        id="name"
        defaultValue=""
        maxLength={24}
        placeholder={translatePageText("onboarding.placeholderName")}
        style={{ margin: "8px 0", width: "100%" }}
        onChange={(e: any) => setInputName(e.target.value)}
      />
      <StyledButton
        fullWidth
        onClick={() => {
          wsUpdateClientData(inputName, myPlayer.id);
        }}
        label={translatePageText("onboarding.continueButton")}
      />
    </div>
  );
};

export default MultiplayerWrapper;
